/**
 * macOS 本机开发文件夹的识别与拒绝（Z13）。
 *
 * 设计稿把五种结果一次画全，其中第五种（已属于别的项目）**标红最要紧**：
 * 静默抢占会让两个项目同时往一个目录写，那不是体验问题，是数据损坏。
 *
 * 整份的硬承诺写在第一行：**你确认之前，这个目录里一个字节都不会变。**
 * 所以这里全部是「看」的函数，没有一个会写。
 */
import { canPickLocalFolder, type Surface } from './platform'

/** 只读探测到的事实。**全部来自看，不来自改。** */
export interface FolderProbe {
  readonly path: string
  readonly exists: boolean
  readonly readable: boolean
  readonly isGitRepo: boolean
  /** 未提交的改动数。不是 Git 仓库时为 0。 */
  readonly dirtyCount: number
  /** 目录里有没有东西。空目录与非空非 Git 是两种不同的结果。 */
  readonly hasFiles: boolean
  /** 已经属于哪个项目。`null` 表示没人占。 */
  readonly ownedByProject: string | null
}

export type FolderVerdict =
  | { readonly kind: 'empty' }
  | { readonly kind: 'git_clean' }
  | { readonly kind: 'not_git' }
  | { readonly kind: 'git_dirty'; readonly count: number }
  | { readonly kind: 'taken'; readonly project: string }
  | { readonly kind: 'no_permission' }
  | { readonly kind: 'stale' }

/**
 * 判这个文件夹属于哪一种。
 *
 * **顺序不能换。** 已被占用要排在所有「能用」的判断之前——
 * 一个已经属于别的项目的 Git 仓库，同时也满足「是干净的 Git 仓库」，
 * 而先判后者就会静默抢占。这一行的次序本身就是那条安全规则。
 */
export function judgeFolder(probe: FolderProbe): FolderVerdict {
  if (!probe.exists) return { kind: 'stale' }
  if (!probe.readable) return { kind: 'no_permission' }
  // 占用判断必须在这里，不能往下挪。见上面那段。
  if (probe.ownedByProject !== null) return { kind: 'taken', project: probe.ownedByProject }
  if (probe.isGitRepo) {
    return probe.dirtyCount > 0 ? { kind: 'git_dirty', count: probe.dirtyCount } : { kind: 'git_clean' }
  }
  return probe.hasFiles ? { kind: 'not_git' } : { kind: 'empty' }
}

/** 这一种能不能往下走。被占、没权限、路径失效三种都不行。 */
export function mayProceed(verdict: FolderVerdict): boolean {
  return verdict.kind !== 'taken' && verdict.kind !== 'no_permission' && verdict.kind !== 'stale'
}

/**
 * 这一种该给哪几个出口。
 *
 * 设计稿点名的两条：权限被拒给「打开系统设置」和「换一个」，
 * **不反复弹系统窗**——反复弹是把一个我们解决不了的问题变成骚扰。
 * 路径失效只给「换一个」，并且要说清项目没坏。
 */
export type FolderExit = 'open_settings' | 'pick_another' | 'none'

export function exitsFor(verdict: FolderVerdict): readonly FolderExit[] {
  switch (verdict.kind) {
    case 'no_permission':
      return ['open_settings', 'pick_another']
    case 'taken':
    case 'stale':
      return ['pick_another']
    default:
      return ['none']
  }
}

/**
 * 这一种要显示哪句话。
 *
 * 每一种都有自己的一句，**没有共用兜底**——共用的那句一定是最含糊的
 * 那句，而这五种里有两种（被占、失效）恰恰要求说得非常具体。
 */
export function copyFor(
  verdict: FolderVerdict,
):
  | 'lulu_workbench.folder.empty'
  | 'lulu_workbench.folder.git_clean'
  | 'lulu_workbench.folder.not_git'
  | 'lulu_workbench.folder.git_dirty'
  | 'lulu_workbench.folder.taken'
  | 'lulu_workbench.folder.no_permission'
  | 'lulu_workbench.folder.stale_path' {
  switch (verdict.kind) {
    case 'empty':
      return 'lulu_workbench.folder.empty'
    case 'git_clean':
      return 'lulu_workbench.folder.git_clean'
    case 'not_git':
      return 'lulu_workbench.folder.not_git'
    case 'git_dirty':
      return 'lulu_workbench.folder.git_dirty'
    case 'taken':
      return 'lulu_workbench.folder.taken'
    case 'no_permission':
      return 'lulu_workbench.folder.no_permission'
    case 'stale':
      return 'lulu_workbench.folder.stale_path'
  }
}

/**
 * 这一整套只在 macOS 上存在。
 *
 * Web 与移动端调到这里应当是**一次编程错误**，而不是一个走得通的分支：
 * 走得通的分支迟早会有人接上，然后 Web 上就冒出一个本地目录入口。
 */
export function folderFlowAvailable(surface: Surface): boolean {
  return canPickLocalFolder(surface)
}

// ── 登记到服务端（POST /environments/checkout）──────────────────────
//
// **绝对路径不进服务端。** openapi 那段描述引的是 PRD §3.6：
// 指纹在设备侧算，`display_label` 只是末段目录名。
//
// 这不是隐私洁癖。一个存了用户绝对路径的服务端，在被人拿到数据库的那天
// 会顺带交出他的用户名、他的目录结构、他在哪台机器上放了什么——
// 而我们从这些信息里得不到任何东西：**我们要判的是「是不是同一个文件夹」，
// 那是一个相等判断，指纹就够了。**

/** 发给服务端的登记请求。**注意这里没有 path 字段，而且不该有。** */
export interface CheckoutRegistration {
  readonly project_id: string
  readonly environment_kind: 'macos'
  /** 这台设备的稳定标识。换一台机器就是另一个 checkout。 */
  readonly device_ref: string
  /** 设备侧算出的稳定指纹。同一个文件夹每次算出来一样，换个文件夹就不一样。 */
  readonly folder_fingerprint: string
  /** 末段目录名，给人看的。**不是路径。** */
  readonly display_label: string
}

/**
 * 末段目录名。
 *
 * 结尾的斜杠要吃掉——`/a/b/` 的末段是 `b` 不是空串，
 * 而空的 display_label 在界面上是一行什么都没有的登记。
 */
export function displayLabelOf(path: string): string {
  const parts = path.split('/').filter((p) => p !== '')
  return parts[parts.length - 1] ?? ''
}

/**
 * 组装登记请求。
 *
 * 指纹与设备标识都是**传进来的**，不在这里算——它们要么来自 Tauri 那侧
 * （真设备信息），要么来自用例。在这里算会让这个函数没法在 jsdom 里验，
 * 而它恰恰是「绝对路径不外泄」那条规则唯一的落点。
 */
export function buildCheckoutRegistration(input: {
  readonly projectId: string
  readonly deviceRef: string
  readonly fingerprint: string
  readonly path: string
}): CheckoutRegistration {
  return {
    project_id: input.projectId,
    environment_kind: 'macos',
    device_ref: input.deviceRef,
    folder_fingerprint: input.fingerprint,
    display_label: displayLabelOf(input.path),
  }
}

/**
 * 服务端 409 之后该显示哪一种。
 *
 * 409 的错误码是 `environment.folder_bound_elsewhere`，语义与本地探测到的
 * `taken` 是同一件事——**但来源不同**：本地那次是我们自己看到的，
 * 这次是服务端告诉我们的，而服务端知道的更多（别的设备上的登记我们看不见）。
 *
 * 对方项目 id 只在请求者看得见它时才进 details，所以这里收 `string | null`：
 * **看不见的时候不编一个名字**，只说「已经属于别的项目」。
 */
export function verdictFromConflict(otherProject: string | null): FolderVerdict {
  return { kind: 'taken', project: otherProject ?? '' }
}

/**
 * 这个请求里有没有夹带绝对路径。
 *
 * 写成一个函数而不是靠评审看：**它是那条隐私规则唯一会红的地方。**
 * 任何一次「顺手把 path 也发过去」都会被它逮到。
 */
export function leaksAbsolutePath(reg: CheckoutRegistration, path: string): boolean {
  return Object.values(reg).some((v) => typeof v === 'string' && v.includes('/') && path.includes(v))
}
